'use client'
import classNames from 'classnames'
import type { DebouncedFuncLeading } from 'lodash'
import throttle from 'lodash/throttle'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import React from 'react'
import { AddressPrompt } from '../address-prompt/AddressPrompt'
import styles from './HeaderMobileSuperapp.module.scss'
import { useDeliveryAddress } from '@/delivery-address/client'
import { useLocalisation } from '@/tools/i18n/client'
import { useIntersectionDetection } from '@/tools/intersection-detection/use-intersection-detection'
import { useErrorHandling } from '@/tools/o11y/client'
import {
  ChevronBackwardIcon,
  IconButton,
  IconButtonSize,
  IconButtonVariants,
  LogoSize,
  LogoSurface,
  LogoVariant,
  MainLogo,
} from '@/tools/ui'
import type { HeaderColorType } from '@/tools/ui/components/header/types'
import type { NavigationMenuContentType } from '@/tools/ui/components/navigation-menu/NavigationMenu'
import { UserSearchInput } from '@/tools/ui/components/user-search-input/UserSearchInput'

interface Props {
  className?: string
  shouldShowAddress?: boolean
  color?: HeaderColorType
  canShowSearch?: boolean
  forceAddressPicker?: boolean
  NavigationMenuContent?: NavigationMenuContentType
  canShowBackButton?: boolean
  children?: React.ReactNode
}

export const HeaderMobileSuperapp: FC<Props> = ({
  className,
  shouldShowAddress,
  forceAddressPicker,
  canShowSearch,
  color,
  canShowBackButton,
  children,
}) => {
  const { getLanguage } = useLocalisation()
  const router = useRouter()
  const [showBackButton, setShowBackButton] = useState(false)
  const language = getLanguage()
  const isBrandHeader = color === 'brand'
  const logoSurface = isBrandHeader ? LogoSurface.onBrand : LogoSurface.onPrimary
  const { execWithErrorTracking, withErrorTracking } = useErrorHandling()
  const [scrolled, setScrolled] = useState(false)
  const { isIntersecting, isAbove } = useIntersectionDetection(
    'hero-container-input',
    'header-mobile'
  )
  const { deliveryAddress, doesDeliveryAddressMatchLocation } = useDeliveryAddress()
  const isIntersectingOrAbove = isIntersecting || isAbove

  const showNavigationSection = shouldShowAddress || showBackButton

  const isSearchVisible = canShowSearch && deliveryAddress && doesDeliveryAddressMatchLocation

  useEffect(() => {
    let handleScroll: DebouncedFuncLeading<() => void> | undefined

    execWithErrorTracking(() => {
      handleScroll = throttle(() => {
        const offset = window.scrollY
        setScrolled(offset > 0)
      }, 200)

      window.addEventListener('scroll', handleScroll)
    })

    return withErrorTracking(() => {
      if (handleScroll) {
        handleScroll.cancel()
        window.removeEventListener('scroll', handleScroll)
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    execWithErrorTracking(() => {
      setShowBackButton(Boolean(canShowBackButton && window.history.length > 1))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowBackButton])

  const handleBack = () => {
    router.back()
  }

  return (
    <div
      className={classNames({
        [styles.intersection]: isIntersectingOrAbove,
        [styles.headerMobile]: true,
        [className ?? '']: true,
        [styles.scrolled]: scrolled && !isBrandHeader,
      })}
      id={'header-mobile'}
    >
      <section className={styles.topContainer}>
        <div className={styles.leftSection}>
          <Link className={styles.logoLink} href={`/${language}`} prefetch={false}>
            <MainLogo
              className={styles.logoImage}
              size={LogoSize.small}
              surface={logoSurface}
              variant={LogoVariant.logotype}
            />
          </Link>
          {children}
        </div>
      </section>
      {showNavigationSection && (
        <section>
          {showBackButton && (
            <IconButton
              aria-labelledby={'back'}
              className={classNames({
                [styles.backButtonNearAddress]: shouldShowAddress,
              })}
              icon={ChevronBackwardIcon}
              size={IconButtonSize.small}
              variant={IconButtonVariants.neutral}
              onClick={handleBack}
            />
          )}
          {shouldShowAddress && (
            <AddressPrompt
              centered={true}
              color={color}
              forceAddressPicker={forceAddressPicker}
              hideArrow={true}
              isIntersecting={isIntersectingOrAbove}
              showLocationScrollDrawer={false}
            />
          )}
        </section>
      )}
      {isSearchVisible && (
        <div className={styles.searchInput}>
          <UserSearchInput />
        </div>
      )}
    </div>
  )
}
